import React, { useState } from 'react';
import { Send, Mail, Phone, Instagram, MapPin, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contacto = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/contact`, formData);
      
      if (response.data.success) {
        setSubmitStatus({
          type: 'success',
          message: '¡Mensaje enviado exitosamente! Te contactaremos pronto.'
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(response.data.message || 'Error al enviar el mensaje');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Hubo un error al enviar tu mensaje. Por favor intenta de nuevo.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'contacto@karirojas.com',
      link: 'mailto:contacto@karirojas.com'
    },
    {
      icon: Phone,
      title: 'WhatsApp',
      value: '+52 56 5491 9535',
      link: 'https://wa.me/525654919535'
    },
    {
      icon: Instagram,
      title: 'Instagram',
      value: '@karirojas.finanzas',
      link: 'https://instagram.com/karirojas.finanzas'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-white to-lilac-light">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-golden/10 text-golden text-sm font-medium tracking-wider rounded-full mb-4">
              CONTACTO
            </span>
            <h1 className="text-4xl sm:text-5xl font-serif font-light text-charcoal mb-6">
              ¿Cómo puedo ayudarte?
            </h1>
            <p className="text-lg text-charcoal/70 max-w-2xl mx-auto leading-relaxed">
              Estoy aquí para resolver tus dudas sobre finanzas personales, 
              mis cursos o cualquier otra consulta que tengas.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <Card className="p-8 sm:p-10 shadow-xl border-cream-dark">
              <h2 className="text-2xl font-serif text-charcoal mb-6">
                Envíame un mensaje
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-charcoal/80 mb-2">
                      Nombre completo *
                    </label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre"
                      className="w-full border-cream-dark focus:ring-golden focus:border-golden"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-charcoal/80 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@email.com"
                      className="w-full border-cream-dark focus:ring-golden focus:border-golden"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-charcoal/80 mb-2">
                      Teléfono (opcional)
                    </label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+52 55 1234 5678"
                      className="w-full border-cream-dark focus:ring-golden focus:border-golden"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-charcoal/80 mb-2">
                      Asunto *
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-cream-dark rounded-md bg-white text-charcoal focus:ring-golden focus:border-golden"
                      data-testid="contact-subject-select"
                    >
                      <option value="">Selecciona un asunto</option>
                      <option value="curso">Información sobre el curso</option>
                      <option value="libro">Información sobre el libro</option>
                      <option value="consultoria">Consultoría personalizada</option>
                      <option value="conferencias">Conferencias y eventos</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-charcoal/80 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Escribe tu mensaje aquí..."
                    className="w-full px-3 py-2 border border-cream-dark rounded-md bg-white text-charcoal focus:ring-golden focus:border-golden resize-none"
                    data-testid="contact-message-textarea"
                  />
                </div>

                {/* Submit Status */}
                {submitStatus && (
                  <div
                    className={`flex items-center gap-3 p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                    data-testid="contact-submit-status"
                  >
                    {submitStatus.type === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <p className="text-sm">{submitStatus.message}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-golden hover:bg-golden-dark text-white py-6 text-base font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send size={20} />
                      Enviar Mensaje
                    </span>
                  )}
                </Button>
              </form>
            </Card>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif text-charcoal mb-6">
                  Información de contacto
                </h2>
                <p className="text-charcoal/70 mb-8 leading-relaxed">
                  También puedes contactarme directamente a través de cualquiera 
                  de estos medios. Respondo en un máximo de 24-48 horas.
                </p>

                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <a
                      key={index}
                      href={info.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-cream-dark hover:shadow-md hover:border-golden/30 transition-all duration-300 group"
                      data-testid={`contact-info-${info.title.toLowerCase()}`}
                    >
                      <div className="w-12 h-12 bg-golden/10 rounded-full flex items-center justify-center group-hover:bg-golden/20 transition-colors">
                        <info.icon className="text-golden" size={22} />
                      </div>
                      <div>
                        <p className="text-sm text-charcoal/60">{info.title}</p>
                        <p className="text-charcoal font-medium group-hover:text-golden transition-colors">
                          {info.value}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Additional Info Card */}
              <Card className="p-6 bg-gradient-to-br from-golden/5 to-lilac/10 border-golden/20">
                <h3 className="text-lg font-serif text-charcoal mb-3">
                  💡 ¿Necesitas ayuda inmediata?
                </h3>
                <p className="text-charcoal/70 text-sm leading-relaxed mb-4">
                  Si tienes una emergencia financiera o necesitas orientación rápida, 
                  te invito a realizar tu diagnóstico financiero gratuito para conocer 
                  tu situación actual.
                </p>
                <Button
                  onClick={() => window.location.href = '/diagnostico'}
                  variant="outline"
                  className="w-full border-golden text-golden hover:bg-golden hover:text-white transition-all duration-300"
                  data-testid="contact-diagnostico-btn"
                >
                  Hacer Diagnóstico Gratis
                </Button>
              </Card>

              {/* Calendly Booking Card */}
              <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 shadow-md">
                <div className="flex items-center gap-3 mb-3">
                  <Calendar className="text-purple-600" size={24} />
                  <h3 className="text-lg font-serif text-charcoal">
                    ¿Quieres reservar una consultoría personalizada conmigo por 90 minutos?
                  </h3>
                </div>
                <p className="text-charcoal/70 text-sm leading-relaxed mb-4">
                  Agenda ahora por sólo <span className="font-bold text-purple-600">$97 USD</span> y con gusto te ayudaré a resolver tus dudas financieras de manera personalizada.
                </p>
                <Button
                  onClick={() => window.open('https://calendly.karirojas.com', '_blank')}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white py-5 font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="contact-calendly-btn"
                >
                  <Calendar className="mr-2" size={18} />
                  Revisar Disponibilidad
                </Button>
              </Card>

              {/* Schedule Info */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-cream-dark">
                <h3 className="text-lg font-serif text-charcoal mb-3">
                  🕐 Horario de atención
                </h3>
                <div className="space-y-2 text-sm text-charcoal/70">
                  <p><span className="font-medium text-charcoal">Lunes a Viernes:</span> 9:00 AM - 6:00 PM (CDMX)</p>
                  <p><span className="font-medium text-charcoal">Sábados:</span> 10:00 AM - 2:00 PM (CDMX)</p>
                  <p className="text-charcoal/50 italic pt-2">
                    Los mensajes fuera de horario serán respondidos al siguiente día hábil.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contacto;
